import moment from 'moment-timezone'
import { paymentType as paymentTypeConst, surveyType } from '@abroad/components'
import { isNumber } from '../../../utils/utility'
import { formatExperienceDate } from '../../../utils/date'
import { formatSchedulingConditionsForAPI } from '../../coachingPlan/schedulingHelpers'

export const toParticipantList = (data) => {
  const list = Array.isArray(data)
    ? data
    : data?.rows || data?.participants || data?.users || data?.data || []
  return Array.isArray(list) ? list : []
}

// Participant rows may arrive as quest participant records ({ userId: {...} })
// or as plain user documents — readers below accept both shapes.
const userOf = (p) =>
  p?.userId && typeof p.userId === 'object' ? p.userId : p?.user || p

export const getParticipantId = (p) =>
  String(
    p?.userId?._id ||
      (typeof p?.userId === 'string' ? p.userId : '') ||
      p?.user?._id ||
      p?._id ||
      '',
  )

export const getParticipantName = (p) => {
  const u = userOf(p)
  const joined = [u?.fname, u?.lname].filter(Boolean).join(' ')
  return u?.fullName || p?.fullName || joined || p?.name || '—'
}

export const getParticipantEmail = (p) => userOf(p)?.email || p?.email || ''

export const getParticipantSignupDate = (p) => {
  const u = userOf(p)
  const date = p?.signupDate || p?.enrolledAt || p?.createdAt || u?.createdAt
  return date ? moment(date).format('MM/DD/YYYY') : '—'
}

const latestSurveyOf = (p) => {
  const u = userOf(p)
  return (p?.surveys || u?.surveys || [])[0] || p?.latestAssessment || null
}

export const getParticipantLatestAssessmentLink = (p) => {
  const survey = latestSurveyOf(p)
  const userId = getParticipantId(p)
  if (!survey?._id || !userId) return null
  const kind = survey.surveyType || survey.type
  const path =
    kind === surveyType.WISDOM
      ? 'wisdom'
      : kind === surveyType.PROSPERITY
      ? 'prosperity'
      : 'survey'
  return `/${path}/${survey._id}/clients/${userId}`
}

export const getParticipantLatestAssessment = (p) => {
  const parts = getParticipantLatestAssessmentParts(p)
  if (!parts) return null
  return parts.date ? `${parts.type} - ${parts.date}` : parts.type
}

export const getParticipantLatestAssessmentParts = (p) => {
  const survey = latestSurveyOf(p)
  if (!survey) return null
  const kind = survey.surveyType || survey.type
  const type =
    kind === surveyType.WISDOM
      ? 'Wisdom Assessment'
      : kind === surveyType.PROSPERITY
      ? 'True Prosperity Assessment'
      : 'Awareness Assessment'
  const takenAt =
    survey.submittedAt || survey.createdAt || survey.date || survey.takenAt
  const date = takenAt ? moment(takenAt).format('MM/DD/YYYY') : ''
  return { type, date }
}

export const getParticipantGuides = (p) => {
  const guides =
    p?.wisdomGuides ||
    p?.guides ||
    p?.coaches ||
    (p?.guide ? [p.guide] : null) ||
    (p?.wisdomGuide ? [p.wisdomGuide] : null) ||
    []
  return Array.isArray(guides) ? guides.filter(Boolean) : []
}

export const getParticipantSOWs = (p) =>
  getParticipantGuides(p)
    .flatMap((guide) => (Array.isArray(guide?.sows) ? guide.sows : []))
    .map((sow) => sow?.planName)
    .filter(Boolean)

export const getGuideId = (guide) =>
  String(typeof guide === 'object' ? guide?.coachId || guide?._id || '' : guide)

export const isParticipantPaired = (p) =>
  getParticipantGuides(p).length > 0 ||
  !!(p?.coachId || p?.wisdomGuideId || p?.guideId)

export const getGuideLabel = (guide) =>
  guide
    ? `${guide.fullName || ''}${guide.isStaffCoach ? ' (Staff Guide)' : ''}`
    : ''

export const getQuestionId = (q) => String(q?._id || q?.id || '')

export const getQuestionTitle = (q) =>
  q?.question || q?.text || q?.title || 'Untitled question'

const answerToText = (value) => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    return value.map(answerToText).filter(Boolean).join(', ')
  }
  if (typeof value === 'object') {
    return Object.values(value).map(answerToText).filter(Boolean).join(', ')
  }
  return String(value).trim()
}

const formatAnswerText = (value, answerType) => {
  const text = answerToText(value)
  if (!text || answerType !== 'date') return text
  return formatExperienceDate(text) || text
}

export const getParticipantAnswer = (p, question) => {
  if (!question) return '—'
  const qid = getQuestionId(question)
  const answers = p?.answers || p?.enrollmentAnswers || []
  const answer = Array.isArray(answers)
    ? answers.find((a) => String(a?.questionId) === qid)
    : answers?.[qid] !== undefined
    ? { answer: answers[qid] }
    : null
  if (!answer) return '—'
  if ((answer.answerType || question.answerType) === 'files') {
    return answerToText(answer.answer) ? 'Uploaded' : 'Not uploaded'
  }
  if (Array.isArray(answer.subAnswers) && answer.subAnswers.length > 0) {
    const subQuestions = question.subQuestions || []
    return (
      answer.subAnswers
        .map((sub) => {
          const subQuestion = subQuestions.find(
            (sq) => String(sq?._id || sq?.id) === String(sub?.subQuestionId),
          )
          return formatAnswerText(sub?.answer, subQuestion?.answerType)
        })
        .filter(Boolean)
        .join(', ') || '—'
    )
  }
  return (
    formatAnswerText(answer.answer, answer.answerType || question.answerType) ||
    '—'
  )
}

const toNumberOrEmpty = (value) =>
  value === '' || value === null || value === undefined ? '' : Number(value)

/**
 * Same `sowData` shape as the network bulk pairing flow (AssignSOWSection).
 */
export const buildBulkSOWData = (values) => {
  const isOneTime = values.paymentCycle === paymentTypeConst.ONETIME
  const isRecurring = values.paymentCycle === paymentTypeConst.RECURRING
  const isPlatformScheduling =
    values.isPlatformScheduling !== false &&
    values.isPlatformScheduling !== 'false'
  const sowType = values.sowType || 'REGULAR'
  const isConditionalSchedulingEnabled =
    isPlatformScheduling && !!values.isConditionalSchedulingEnabled

  const params = {
    isPaymentRequired: values.isPaymentRequired === 'true',
    planName: values.planName?.trim(),
    paymentCycle: values.paymentCycle,
    ...(!isRecurring && {
      onetimeAmount: toNumberOrEmpty(values.onetimeAmount),
    }),
    ...((isOneTime || (values.invoiceEvery && values.numberOfBillingCycle)) && {
      totalDurationOfContract: values.totalDurationOfContract,
    }),
    ...(isRecurring && {
      invoiceEvery: isNumber(values.invoiceEvery)
        ? Number(values.invoiceEvery)
        : '',
      numberOfBillingCycle: toNumberOrEmpty(values.numberOfBillingCycle),
      subscriptionAmount: toNumberOrEmpty(values.subscriptionAmount),
      upfrontFee: toNumberOrEmpty(values.upfrontFee),
    }),
    typeOfCoachingCycle: values.typeOfCoachingCycle,
    billingIndefinite: !isOneTime
      ? !values.numberOfBillingCycle
      : !values.totalDurationOfContract,
    sessionPerCoachingCycle: toNumberOrEmpty(values.sessionPerCoachingCycle),
    numberOfCoachingCycle: Number(values.numberOfCoachingCycle),
    sessionDuration: toNumberOrEmpty(values.sessionDuration),
    includeGrowthSession: !!values.includeGrowthSession,
    ...(values.includeGrowthSession && {
      growthSessionDuration: toNumberOrEmpty(values.growthSessionDuration),
      growthCoachingRateType: values.growthCoachingRateType,
      growthCoachingRate: toNumberOrEmpty(values.growthCoachingRate),
    }),
    coachingRate: toNumberOrEmpty(values.coachingRate),
    coachingRateType: values.coachingRateType,
    ...((values.numberOfBillingCycle || isOneTime) && {
      totalSession: toNumberOrEmpty(values.totalSession),
    }),
    timeZone: values.clientTimeZone,
    planStartDate: values.planStartDate,
    ...(values.isPaymentRequired === 'true' && {
      isReadyForPayment: values.isReadyForPayment || false,
    }),
    sowType,
    ...(sowType === 'QUEST' &&
      isPlatformScheduling && { questId: values.questId }),
    ...(isConditionalSchedulingEnabled && {
      isConditionalSchedulingEnabled: true,
      schedulingConditions: formatSchedulingConditionsForAPI(
        values.schedulingConditions,
      ),
    }),
    isPlatformScheduling,
    includeBioInMail: values.includeBioInMail === true,
  }

  Object.keys(params).forEach((key) => {
    if (params[key] === '' || params[key] === undefined) delete params[key]
  })

  return params
}
